import {
  createApi,
  fetchBaseQuery,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/dist/query/react";
import { toast } from "react-toastify";

import * as devConfig from "../app/configs/devConfig";
import { getAuthHeader } from "../app/globalState/GlobalAuthState";
import {
  AddEmailToWorkModel,
  TestModel,
} from "../factories/emails/models/add-email-to-work.model";
import { EmailReplyModel } from "../factories/emails/models/email-reply.model";
import { Email } from "../factories/emails/models/emails.models";
import { sendEmailModel } from "../factories/emails/models/send-email.model";
import { EmailUpdateModel } from "../factories/emails/models/update-email.model";
import { TFolder } from "../types/types";

type LinkedContactEmailGroup = {
  data?: Email[] | null;
};

type LinkedContactEmailsResponse = {
  data?: Array<Email | LinkedContactEmailGroup> | null;
  next_cursor: string;
};

const isLinkedContactEmailGroup = (
  item: Email | LinkedContactEmailGroup,
): item is LinkedContactEmailGroup => {
  return "data" in item;
};

const EMAIL_RECONNECT_TOAST_ID = "email-reconnect-required";
const EMAIL_RECONNECT_ERROR_TEXT =
  "Grant for this User doesn't exists. Try authorizing again.";
const EMAIL_RECONNECT_TOAST_TEXT =
  "Please reconnect your email account to continue.";
const EMAIL_RECONNECT_TOAST_ENDPOINTS = new Set([
  "sendEmail",
  "replyEmail",
  "getContactEmails",
  "getLinkedWork",
]);

const getErrorText = (data: unknown): string => {
  if (typeof data === "string") {
    return data;
  }

  if (!data) {
    return "";
  }

  try {
    return JSON.stringify(data);
  } catch {
    return "";
  }
};

const isEmailReconnectError = (error: FetchBaseQueryError) => {
  const errorText = getErrorText(error.data).toLowerCase();

  return (
    error.status === 401 ||
    error.status === 403 ||
    errorText.includes(EMAIL_RECONNECT_ERROR_TEXT.toLowerCase()) ||
    errorText.includes("try authorizing again") ||
    errorText.includes("unauthorized")
  );
};

const emailsBaseQuery = fetchBaseQuery({
  baseUrl: `${devConfig.getServerBaseUrlForEnv()}`,
  prepareHeaders: (headers) => {
    const authHeader = getAuthHeader();
    headers.set("Authorization", authHeader.Authorization);
    return headers;
  },
});

const emailsBaseQueryWithReconnectToast: typeof emailsBaseQuery = async (
  args,
  api,
  extraOptions,
) => {
  const result = await emailsBaseQuery(args, api, extraOptions);

  if (
    result.error &&
    EMAIL_RECONNECT_TOAST_ENDPOINTS.has(api.endpoint) &&
    isEmailReconnectError(result.error)
  ) {
    toast.error(EMAIL_RECONNECT_TOAST_TEXT, {
      toastId: EMAIL_RECONNECT_TOAST_ID,
    });
  }

  return result;
};

export const emailsSlice = createApi({
  reducerPath: "emailsSlice",
  tagTypes: ["Email"],
  baseQuery: emailsBaseQueryWithReconnectToast,
  endpoints: (build) => ({
    getEmails: build.query<
      { data: Email[]; nextPageValue: string },
      {
        userId: string;
        folderId: string;
        pageNumber: string | undefined;
      }
    >({
      query: ({ userId, folderId, pageNumber }) => ({
        url: "api/emails/get-messages",
        params: { userId, limit: 30, folderId, pageNumber },
      }),
      transformResponse: (res: { data: Email[]; next_cursor: string }) => {
        return { data: res?.data || [], nextPageValue: res.next_cursor };
      },
      transformErrorResponse: (res: FetchBaseQueryError): string => {
        const error = res?.data as string;
        return error || "";
      },
      providesTags: () => ["Email"],
    }),
    getContactEmails: build.query<
      { data: Email[]; nextPageValue: string },
      {
        userId: string;
        pageNumber: string | undefined;
        contactEmail: string;
        folder: string;
        orgId: string;
      }
    >({
      query: ({ userId, pageNumber, contactEmail, folder, orgId }) => ({
        url: "api/emails/get-linked-contact",
        params: {
          userId,
          limit: 30,
          pageNumber,
          contactEmail,
          folder,
          orgId,
        },
      }),
      transformResponse: (res: LinkedContactEmailsResponse) => {
        const result: Email[] = [];

        res?.data?.forEach((item) => {
          if (isLinkedContactEmailGroup(item)) {
            item.data?.forEach((email) => result.push(email));
            return;
          }

          result.push(item);
        });

        return {
          data: result.sort((a, b) => b.date - a.date) || [],
          nextPageValue: res.next_cursor,
        };
      },
      transformErrorResponse: (res: FetchBaseQueryError): string => {
        const error = res?.data as string;
        return error || "";
      },
      providesTags: () => ["Email"],
    }),
    getRedirectUrl: build.mutation<
      any,
      { provider: string; returnUrl: string; userId: string }
    >({
      query: (params) => ({
        url: "nylas/get-auth-url",
        params,
        responseHandler: (response: { text: () => any }) => response.text(),
      }),
    }),
    getEmailFolders: build.query<TFolder[], { userId: string }>({
      query: ({ userId }) => ({
        url: "api/emails/get-folders",
        method: "Get",
        params: { userId },
      }),
      transformResponse: (res: { data: TFolder[] }) => {
        return res?.data || [];
      },
      transformErrorResponse: (res: FetchBaseQueryError): string => {
        const error = res?.data as string;
        return error || "";
      },
      providesTags: () => ["Email"],
    }),

    searchEmail: build.query<
      { data: Email[]; nextPageValue: string },
      { userId: string; pageNumber?: string; query: string }
    >({
      query: ({ userId, pageNumber, query }) => ({
        url: "api/emails/search",
        method: "Get",
        params: { userId, limit: 30, pageNumber, query },
      }),
      transformResponse: (res: { data: Email[]; next_cursor: string }) => {
        return { data: res?.data || [], nextPageValue: res.next_cursor };
      },
      transformErrorResponse: (res: FetchBaseQueryError): string => {
        const error = res?.data as string;
        return error || "";
      },
      providesTags: () => ["Email"],
    }),
    messageByIds: build.query<
      { data: Email[] },
      { userId: string; messageIds: string[] }
    >({
      query: ({ userId, messageIds }) => ({
        url: "api/emails/message-by-ids",
        method: "Get",
        params: { userId, messageIds },
      }),

      transformErrorResponse: (res: FetchBaseQueryError): string => {
        const error = res?.data as string;
        return error || "";
      },
      providesTags: () => ["Email"],
    }),
    sendEmail: build.mutation<
      { data: Email },
      { userId: string; body: sendEmailModel }
    >({
      query: ({ userId, body }) => ({
        url: "api/emails/send-message",
        method: "Post",
        body,
        params: { userId },
      }),
      invalidatesTags: ["Email"],
    }),
    replyEmail: build.mutation<
      unknown,
      { userId: string; body: EmailReplyModel }
    >({
      query: ({ userId, body }) => ({
        url: "api/emails/reply-message",
        method: "Post",
        body,
        params: { userId },
      }),
      invalidatesTags: ["Email"],
    }),
    starredEmail: build.mutation<
      { data: Email },
      { body: EmailUpdateModel; userId: string; messageId: string }
    >({
      query: ({ body, userId, messageId }) => ({
        url: "api/emails/update-message",
        method: "Put",
        body,
        params: { userId, messageId },
      }),
      invalidatesTags: ["Email"],
    }),

    addEmailToWork: build.mutation<unknown, { body: AddEmailToWorkModel }>({
      query: ({ body }) => ({
        url: "api/emails/add-email-to-work",
        method: "Post",
        body,
      }),
      invalidatesTags: ["Email"],
    }),

    getWorkEmail: build.query<
      { data: Email[]; nextPageValue: string },
      { userId: string; workId: string }
    >({
      query: ({ userId, workId }) => ({
        url: "api/emails/fetch-work-email",
        method: "Get",
        params: { userId, workId },
      }),
      transformResponse: (res: {
        data: { data: Email[] }[];
        next_cursor: string;
      }) => {
        const result: Email[] = [];

        res?.data.forEach(
          (item) => item.data && item?.data.map((i) => result.push(i)),
        );
        return {
          data: result.sort((a, b) => b.date - a.date) || [],
          nextPageValue: res.next_cursor,
        };
      },
      providesTags: ["Email"],
    }),
    getLinkedWork: build.query<string, { threadId: string }>({
      query: ({ threadId }) => ({
        url: "api/emails/get-linked-work",
        method: "Get",
        params: { threadId },
        responseHandler: (response) => response.text(),
      }),
      providesTags: ["Email"],
    }),
    disconnectEmail: build.mutation<unknown, { userId: string }>({
      query: (params) => ({
        url: "api/emails/disconnect",
        method: "Delete",
        params,
      }),
      invalidatesTags: ["Email"],
    }),
    deleteEmail: build.mutation<unknown, { userId: string; messageId: string }>(
      {
        query: (params) => ({
          url: "api/emails/delete-message",
          method: "DELETE",
          params,
        }),
        invalidatesTags: ["Email"],
      },
    ),
    downloadAttachments: build.query<
      string,
      { userId: string; messageId: string; attachmentId: string }
    >({
      query: ({ userId, messageId, attachmentId }) => ({
        url: "api/emails/download-attachment",
        method: "Get",
        params: { userId, messageId, attachmentId },
        responseHandler: (response) => response.text(),
      }),

      transformErrorResponse: (res: FetchBaseQueryError): string => {
        const error = res?.data as string;
        return error || "";
      },
      providesTags: () => ["Email"],
    }),
    downloadMetadata: build.query<
      unknown,
      { userId: string; messageId: string; attachmentId: string }
    >({
      query: ({ userId, messageId, attachmentId }) => ({
        url: "api/emails/attachment-metadata",
        method: "Get",
        params: { userId, messageId, attachmentId },

        responseHandler: (response) => response.text(),
      }),

      transformErrorResponse: (res: FetchBaseQueryError): string => {
        const error = res?.data as string;
        return error || "";
      },
      providesTags: () => ["Email"],
    }),
    getStarred: build.query<
      { data: Email[]; nextPageValue: string },
      { userId: string }
    >({
      query: ({ userId }) => ({
        url: "api/emails/filter-starred",
        method: "GET",
        params: { userId },
      }),
      providesTags: () => ["Email"],
    }),
    unlinkEmail: build.mutation<unknown, { body: AddEmailToWorkModel }>({
      query: ({ body }) => ({
        url: "api/emails/unlink-work",
        method: "Delete",
        body,
      }),
      invalidatesTags: ["Email"],
    }),
    addTest: build.mutation<unknown, TestModel>({
      query: (body) => ({
        url: "/nylas/add-webhook-destination",
        method: "Post",
        body,
      }),
      invalidatesTags: ["Email"],
    }),
  }),
});
export const {
  useGetEmailsQuery,
  useLazyGetEmailsQuery,
  useLazyGetStarredQuery,
  useGetContactEmailsQuery,
  useLazyGetContactEmailsQuery,
  useGetRedirectUrlMutation,
  useGetEmailFoldersQuery,
  useGetWorkEmailQuery,
  useLazyGetLinkedWorkQuery,
  useAddEmailToWorkMutation,
  useLazySearchEmailQuery,
  useSendEmailMutation,
  useReplyEmailMutation,
  useStarredEmailMutation,
  useDisconnectEmailMutation,
  useDeleteEmailMutation,
  useLazyDownloadAttachmentsQuery,
  useLazyDownloadMetadataQuery,
  useUnlinkEmailMutation,
  useLazyMessageByIdsQuery,
} = emailsSlice;
